<template>
    <div :class="{ comment: true, deleting: submittingDelete }">
        <div class="comment-actions dropdown">
            <a class="dropdown-toggle" data-toggle="dropdown" href="#">
                <i class="fa fa-chevron-down"></i>
            </a>
            <ul class="dropdown-menu dropdown-menu-right">
                <li>
                    <button
                        class="clear-button"
                        type="button"
                        v-if="canEdit"
                        @click="toggleEditForm">
                        Edit
                    </button>
                </li>
                <li>
                    <button
                        class="clear-button"
                        type="button"
                        @click="toggleReplyForm">
                        Reply
                    </button>
                </li>
                <li
                    class="divider"
                    v-if="canDelete"></li>
                <li v-if="canDelete">
                    <button
                        class="clear-button danger"
                        type="button"
                        :disabled="submittingDelete"
                        @click="emitDelete">
                        Delete
                    </button>
                </li>
            </ul>
        </div>

        <div class="comment-author">
            <router-link :to="{ name: 'galleries', query: { author: comment.author.id } }">
                {{ comment.author.name }}
            </router-link>
        </div>

        <gallery-comment-form
            ref="editForm"
            placeholder="Edit..."
            submitText="Save"
            v-if="editFormVisible"
            :cancelable="true"
            :initialText="comment.text"
            @cancel="toggleEditForm"
            @submit="emitEdit"
            @focus="$emit('focus')"
            @blur="$emit('blur')">
        </gallery-comment-form>
        <div
            class="comment-text"
            v-html="$options.filters.linkify(escapeHtml(comment.text))"
            v-else>
        </div>

        <div class="comment-like-actions">
            <button
                class="clear-button"
                type="button"
                :disabled="submittingLike"
                v-if="!hasLikedComment()"
                @click="emitLike">
                Like
            </button>
            <button
                class="clear-button"
                type="button"
                :disabled="submittingLike"
                v-else
                @click="emitUnlike">
                Unlike
            </button>
            <span v-if="comment.likes.length > 0">{{ formatLikesText(comment.likes.length) }}</span>
        </div>

        <div class="reply-section">
            <ol>
                <li v-for="reply in comment.replies">
                    <gallery-comment
                        class="comment"
                        :comment="reply"
                        @like="forwardLike"
                        @unlike="forwardUnlike"
                        @edit="forwardEdit"
                        @reply="forwardReply"
                        @delete="forwardDelete"
                        @focus="$emit('focus')"
                        @blur="$emit('blur')">
                    </gallery-comment>
                </li>
            </ol>
            <gallery-comment-form
                ref="replyForm"
                placeholder="Reply..."
                v-if="replyFormVisible"
                :cancelable="true"
                @cancel="toggleReplyForm"
                @submit="emitReply"
                @focus="$emit('focus')"
                @blur="$emit('blur')">
            </gallery-comment-form>
        </div>
    </div>
</template>

<script>
    import GalleryCommentForm from './GalleryCommentForm';

    export default {
        name: 'gallery-comment',

        components: {
            GalleryCommentForm
        },

        props: {
            comment: {
                type: Object,
                validator: comment => {
                    return (
                        typeof comment.text === 'string' &&
                        typeof comment.author === 'object' &&
                        typeof comment.author.id === 'number' &&
                        typeof comment.author.name === 'string' &&
                        Array.isArray(comment.likes),
                        Array.isArray(comment.replies)
                    );
                }
            }
        },

        data() {
            return {
                submittingLike: false,
                submittingDelete: false,
                editFormVisible: false,
                replyFormVisible: false
            };
        },

        methods: {
            getUserId() {
                return parseInt(localStorage.user_id);
            },

            getUserRoles() {
                return JSON.parse(localStorage.roles);
            },

            emitLike() {
                if (this.submittingLike) {
                    return;
                }
                const setSubmitting = submitting => this.submittingLike = submitting;
                this.$emit('like', this.comment.id, setSubmitting);
            },

            emitUnlike() {
                if (this.submittingLike) {
                    return;
                }
                const setSubmitting = submitting => this.submittingLike = submitting;
                this.$emit('unlike', this.comment.id, setSubmitting);
            },

            emitEdit(text, setSubmitting, clearText) {
                const hideEditForm = () => this.editFormVisible = false;
                this.$emit('edit', this.comment.id, text, setSubmitting, clearText, hideEditForm);
            },

            emitReply(text, setSubmitting, clearText) {
                const hideReplyForm = () => this.replyFormVisible = false;
                this.$emit('reply', this.comment.id, text, setSubmitting, clearText, hideReplyForm);
            },

            emitDelete() {
                const setSubmitting = submitting => this.submittingDelete = submitting;
                this.$emit('delete', this.comment.id, setSubmitting);
            },

            forwardLike() {
                this.$emit('like', ...arguments);
            },

            forwardUnlike() {
                this.$emit('unlike', ...arguments);
            },

            forwardEdit() {
                this.$emit('edit', ...arguments);
            },

            forwardReply() {
                this.$emit('reply', ...arguments);
            },

            forwardDelete() {
                this.$emit('delete', ...arguments);
            },

            async toggleEditForm() {
                this.editFormVisible = !this.editFormVisible;
                if (this.editFormVisible) {
                    await this.$nextTick();
                    this.$refs.editForm.focus();
                }
            },

            async toggleReplyForm() {
                this.replyFormVisible = !this.replyFormVisible;
                if (this.replyFormVisible) {
                    await this.$nextTick();
                    this.$refs.replyForm.focus();
                }
            },

            hasLikedComment() {
                const userId = this.getUserId();
                return !!this.comment.likes.find(l => l.id === userId);
            },

            formatLikesText(likeCount) {
                if (likeCount === 1) {
                    return '1 Like';
                } else {
                    return `${likeCount} Likes`;
                }
            },

            escapeHtml(text) {
                text = text || '';
                text = text.replace(/&/g, '&amp;');
                text = text.replace(/</g, '&lt;');
                text = text.replace(/>/g, '&gt;');
                text = text.replace(/"/g, '&quot;');
                return text;
            }
        },

        computed: {
            canEdit() {
                const isAuthor = this.getUserId() === this.comment.author.id;
                const isMod = !!this.getUserRoles().find(r => {
                    return r === 'OnlineDirector' || r === 'Supervisor';
                });
                return isAuthor || isMod;
            },

            canDelete() {
                const isAuthor = this.getUserId() === this.comment.author.id;
                const isMod = !!this.getUserRoles().find(r => {
                    return r === 'OnlineDirector' || r === 'Supervisor';
                });
                return isAuthor || isMod;
            }
        }
    }
</script>

<style lang="scss" scoped>
    .clear-button {
        border: none;
        background: none;
    }

    .dropdown-menu {
        background-color: #363636 !important;
    }

    .comment {
        position: relative;
    }
    .comment.deleting {
        opacity: 0.6;
    }
    .comment-actions {
        position: absolute;
        top: 0;
        right: 0;
        .dropdown-toggle {
            color: #acacac;
            &:hover {
                color: #33ccff;
            }
        }
        .dropdown-menu {
            min-width: 0;
            width: 70px;
            font-size: 12px;
        }
        .divider {
            margin: 5px 0;
            background-color: #666;
        }
        button {
            display: block;
            width: 100%;
        }
        button:hover {
            color: #33ccff;
        }
        button.danger:hover {
            color: #ff0033;
        }
    }
    .comment-author {
        margin-bottom: 6px;
    }
    .comment-like-actions {
        margin-top: 8px;
        font-size: 12px;
        button {
            color: #33ccff;
            padding: 1px 0;
            margin-right: 5px;
        }
        button:hover {
            text-decoration: underline;
        }
        button[disabled] {
            color: #b0b0b0;
        }
        button[disabled]:hover {
            text-decoration: none;
        }
    }

    .reply-section {
        border-left: 1px solid #555;
        padding-left: 20px;
        margin-top: 20px;

        ol {
            list-style-type: none;
            padding: 0;
            margin: 0;
        }
        .comment {
            margin-bottom: 20px;
        }
    }
</style>



// WEBPACK FOOTER //
// GalleryComment.vue?38ce9540